import { useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight, ArrowUpRight, BadgeCheck, ShieldCheck,
  Clock3, Trophy, ChevronDown, Phone,
} from "lucide-react";
import { toast } from "sonner";
import { Reveal, MaskedLine } from "../components/Reveal";
import { SITE, SERVICES, IMAGES } from "../lib/site";

const MARQUEE_ITEMS = [
  "WEB DEVELOPMENT", "MOBILE APPS", "CLOUD & DEVOPS", "CYBERSECURITY",
  "AI AUTOMATION", "WHATSAPP BUSINESS API", "UI/UX DESIGN", "IT CONSULTING",
];

const STATS = [
  { icon: Trophy, value: "500+", label: "Projects Delivered" },
  { icon: BadgeCheck, value: "250+", label: "Happy Clients" },
  { icon: Clock3, value: "99.9%", label: "Uptime SLA" },
  { icon: ShieldCheck, value: "24×7", label: "Support & Monitoring" },
];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 110]);

  return (
    <section ref={ref} className="relative bg-navy text-white overflow-hidden grain" data-testid="hero-section">
      <div className="absolute inset-0 hero-grid" />
      <div className="absolute -top-40 -right-40 w-[36rem] h-[36rem] rounded-full bg-royal/25 blur-[130px]" />
      <div className="absolute -bottom-52 -left-40 w-[32rem] h-[32rem] rounded-full bg-gold/15 blur-[130px]" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-16 sm:pt-24 pb-20">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 border border-gold/40 bg-gold/10 rounded-full px-4 py-1.5 text-xs font-semibold tracking-[0.18em] uppercase text-gold-light"
              data-testid="hero-trust-badge"
            >
              <BadgeCheck size={14} />
              Trusted IT Company — Delhi, India
            </motion.div>

            <h1 className="mt-7 font-display font-black tracking-tight leading-[0.95]" data-testid="hero-heading">
              <MaskedLine delay={0.15} className="text-5xl sm:text-7xl lg:text-8xl">
                <span className="text-white">RICHIE</span> <span className="text-gold">RICH</span>
              </MaskedLine>
              <MaskedLine delay={0.3} className="text-2xl sm:text-4xl lg:text-5xl mt-3 text-slate-300 font-extrabold">
                Engineering Digital
              </MaskedLine>
              <MaskedLine delay={0.42} className="text-2xl sm:text-4xl lg:text-5xl text-stroke-light font-extrabold">
                Excellence for Business
              </MaskedLine>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.6 }}
              className="mt-7 max-w-xl text-base sm:text-lg text-slate-400 leading-relaxed"
              data-testid="hero-subtext"
            >
              A Delhi-based IT company building enterprise websites, mobile apps, cloud infrastructure and
              cybersecurity systems — trusted by growing businesses across India and beyond.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.75 }}
              className="mt-9 flex flex-wrap items-center gap-4"
            >
              <Link
                to="/contact"
                data-testid="hero-quote-cta"
                className="inline-flex items-center gap-2.5 bg-gold hover:bg-gold-light text-navy font-bold px-7 py-3.5 rounded-full transition-all duration-300 hover:-translate-y-1 shadow-xl shadow-gold/25"
              >
                Get a Free Quote
                <ArrowRight size={18} strokeWidth={2.4} />
              </Link>
              <Link
                to="/services"
                data-testid="hero-services-cta"
                className="inline-flex items-center gap-2 border border-white/25 hover:border-gold hover:text-gold text-white font-semibold px-7 py-3.5 rounded-full transition-all duration-300 hover:-translate-y-1"
              >
                Explore Services
                <ArrowRight size={17} />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.95 }}
              className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-sm text-slate-400"
            >
              <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-emerald-400" /> Delhi, India HQ</span>
              <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-emerald-400" /> {SITE.email}</span>
              <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-emerald-400" /> {SITE.phoneDisplay}</span>
            </motion.div>
          </div>

          <div className="lg:col-span-5 relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="relative"
            >
              <div className="absolute -inset-3 rounded-[2rem] border border-gold/25 rotate-2" />
              <div className="relative rounded-[1.75rem] overflow-hidden border border-white/15 shadow-2xl">
                <motion.img
                  src={IMAGES.hero}
                  alt="RICHIE RICH engineering team at work"
                  className="w-full h-[26rem] object-cover"
                  style={{ y: imgY, scale: 1.15 }}
                  data-testid="hero-image"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy/80 via-transparent to-transparent" />
                <div className="absolute bottom-5 left-5 right-5 flex items-center justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] text-gold-light font-semibold">Since Day One</p>
                    <p className="font-display font-bold text-white text-lg">Code. Ship. Scale.</p>
                  </div>
                  <div className="w-11 h-11 rounded-full bg-gold flex items-center justify-center animate-float">
                    <ArrowUpRight className="text-navy" size={20} strokeWidth={2.6} />
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Marquee = () => (
  <div className="bg-gold py-4 overflow-hidden border-y border-navy/10" data-testid="services-marquee">
    <div className="flex whitespace-nowrap animate-marquee w-max">
      {[0, 1].map((copy) => (
        <div key={copy} className="flex items-center" aria-hidden={copy === 1}>
          {MARQUEE_ITEMS.map((item) => (
            <span key={`${copy}-${item}`} className="flex items-center font-display font-extrabold text-navy text-sm sm:text-base tracking-[0.2em]">
              <span className="px-6">{item}</span>
              <span className="w-2 h-2 rotate-45 bg-navy/70 inline-block" />
            </span>
          ))}
        </div>
      ))}
    </div>
  </div>
);

const Stats = () => (
  <section className="bg-ivory py-16" data-testid="stats-section">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-2 lg:grid-cols-4 gap-6">
      {STATS.map((s, i) => (
        <Reveal key={s.label} delay={i * 0.08}>
          <div className="bg-white rounded-2xl border border-slate-200 p-7 hover:border-gold/60 hover:-translate-y-1 transition-all duration-300 shadow-sm" data-testid={`stat-card-${i}`}>
            <s.icon className="text-royal" size={26} strokeWidth={2} />
            <p className="mt-4 font-display text-3xl sm:text-4xl font-black text-ink">{s.value}</p>
            <p className="mt-1 text-sm text-slate-500 font-medium">{s.label}</p>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

const ServicesBento = () => (
  <section className="bg-ivory pb-24" data-testid="services-bento">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <Reveal>
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-12">
          <div>
            <p className="text-xs uppercase tracking-[0.28em] font-bold text-royal">What We Do</p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-black text-ink tracking-tight">
              Core IT Services
            </h2>
          </div>
          <Link
            to="/services"
            data-testid="bento-view-all-link"
            className="inline-flex items-center gap-2 font-semibold text-royal hover:text-navy transition-colors"
          >
            View all services <ArrowRight size={17} />
          </Link>
        </div>
      </Reveal>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <Reveal className="md:col-span-2" delay={0.05}>
          <div className="group relative h-72 md:h-80 rounded-3xl overflow-hidden" data-testid="bento-card-featured">
            <img src={IMAGES.developer} alt="Software engineering at RICHIE RICH" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/40 to-transparent" />
            <div className="absolute bottom-0 p-7">
              <p className="text-xs uppercase tracking-[0.22em] text-gold-light font-semibold">Flagship</p>
              <h3 className="mt-2 font-display text-2xl sm:text-3xl font-extrabold text-white">Web & Mobile Development</h3>
              <p className="mt-2 text-slate-300 text-sm max-w-md">Enterprise-grade websites and apps engineered on modern stacks — fast, secure and built to industry standards.</p>
            </div>
          </div>
        </Reveal>

        {SERVICES.slice(2, 5).map((s, i) => (
          <Reveal key={s.id} delay={0.1 + i * 0.07}>
            <div className="group bg-white rounded-3xl border border-slate-200 p-7 h-full hover:border-royal/50 hover:shadow-xl hover:shadow-royal/10 hover:-translate-y-1.5 transition-all duration-300" data-testid={`bento-card-${s.id}`}>
              <div className="w-12 h-12 rounded-xl bg-navy flex items-center justify-center group-hover:bg-royal transition-colors duration-300">
                <s.icon className="text-gold" size={22} strokeWidth={2.2} />
              </div>
              <h3 className="mt-5 font-display text-xl font-bold text-ink">{s.title}</h3>
              <p className="mt-2 text-sm text-slate-500 leading-relaxed">{s.short}</p>
              <Link to="/services" className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-royal">
                Learn more <ArrowUpRight size={15} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </Link>
            </div>
          </Reveal>
        ))}

        <Reveal className="md:col-span-2" delay={0.15}>
          <div className="group relative h-64 rounded-3xl overflow-hidden" data-testid="bento-card-security">
            <img src={IMAGES.security} alt="Cybersecurity operations" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/70 to-transparent" />
            <div className="absolute inset-0 p-7 flex flex-col justify-center max-w-sm">
              <h3 className="font-display text-2xl font-extrabold text-white">Cybersecurity & Compliance</h3>
              <p className="mt-2 text-slate-300 text-sm">Audits, penetration testing and ISO 27001-aligned security practices protecting your business round the clock.</p>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.2}>
          <div className="bg-navy rounded-3xl p-7 h-full flex flex-col justify-between border border-white/10" data-testid="bento-card-whatsapp">
            <div>
              <p className="text-xs uppercase tracking-[0.22em] text-gold font-semibold">Official API</p>
              <h3 className="mt-3 font-display text-xl font-bold text-white">WhatsApp Business API</h3>
              <p className="mt-2 text-sm text-slate-400 leading-relaxed">Official WhatsApp Business API onboarding, broadcast messaging and chatbot automation.</p>
            </div>
            <Link
              to="/contact"
              data-testid="bento-enquire-cta"
              className="mt-5 inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-light text-navy text-sm font-bold py-3 rounded-full transition-colors"
            >
              Enquire Now <ArrowRight size={16} />
            </Link>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

const Estimator = () => {
  const navigate = useNavigate();
  const [service, setService] = useState(SERVICES[0].title);
  const [budget, setBudget] = useState("₹50,000 – ₹1,50,000");
  const [timeline, setTimeline] = useState("2 – 4 weeks");

  const submit = () => {
    toast.success("Opening the enquiry form with your estimate details...");
    navigate("/contact", { state: { service, budget, timeline } });
  };

  const selectCls =
    "w-full appearance-none bg-white border border-slate-300 rounded-xl px-4 py-3.5 text-sm font-medium text-ink focus:outline-none focus:ring-2 focus:ring-royal/50 focus:border-royal transition";

  return (
    <section className="bg-navy py-24 relative overflow-hidden grain" data-testid="estimator-section">
      <div className="absolute inset-0 hero-grid" />
      <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-royal/20 blur-[120px]" />
      <div className="relative max-w-5xl mx-auto px-5 sm:px-8">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.28em] font-bold text-gold">Instant Quote</p>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight">
            Project Cost Estimator
          </h2>
          <p className="mt-4 text-slate-400 max-w-xl">
            Answer three quick questions — we will carry your selection into the enquiry form and our team will
            share a tailored quote.
          </p>
        </Reveal>

        <Reveal delay={0.12}>
          <div className="mt-10 bg-white/5 backdrop-blur-md border border-white/10 rounded-3xl p-7 sm:p-9">
            <div className="grid sm:grid-cols-3 gap-5">
              {[
                { label: "Service", value: service, set: setService, options: SERVICES.map((s) => s.title), id: "service" },
                { label: "Budget Range", value: budget, set: setBudget, options: ["Under ₹50,000", "₹50,000 – ₹1,50,000", "₹1,50,000 – ₹5,00,000", "₹5,00,000+"], id: "budget" },
                { label: "Timeline", value: timeline, set: setTimeline, options: ["Urgent (< 2 weeks)", "2 – 4 weeks", "1 – 3 months", "3+ months"], id: "timeline" },
              ].map((f) => (
                <div key={f.id}>
                  <label className="block text-xs uppercase tracking-[0.2em] font-semibold text-slate-400 mb-2.5">{f.label}</label>
                  <div className="relative">
                    <select
                      value={f.value}
                      onChange={(e) => f.set(e.target.value)}
                      className={selectCls}
                      data-testid={`estimator-select-${f.id}`}
                    >
                      {f.options.map((o) => (
                        <option key={o} value={o}>{o}</option>
                      ))}
                    </select>
                    <ChevronDown size={16} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                  </div>
                </div>
              ))}
            </div>
            <button
              onClick={submit}
              data-testid="estimator-submit-button"
              className="mt-8 w-full inline-flex items-center justify-center gap-2.5 bg-gold hover:bg-gold-light text-navy font-bold py-4 rounded-full transition-all duration-300 hover:-translate-y-0.5 shadow-xl shadow-gold/25"
            >
              Request Free Quote
              <ArrowRight size={18} strokeWidth={2.4} />
            </button>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const WhyUs = () => (
  <section className="bg-ivory py-24" data-testid="why-us-section">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
      <Reveal>
        <div className="relative">
          <div className="absolute -inset-3 rounded-[2rem] border-2 border-gold/40 -rotate-1" />
          <img src={IMAGES.team} alt="RICHIE RICH team collaboration" className="relative rounded-[1.75rem] w-full h-[26rem] object-cover shadow-2xl" data-testid="why-us-image" />
        </div>
      </Reveal>
      <div>
        <Reveal>
          <p className="text-xs uppercase tracking-[0.28em] font-bold text-royal">Why RICHIE RICH</p>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl font-black text-ink tracking-tight">
            A Technology Partner, Not Just a Vendor
          </h2>
        </Reveal>
        <div className="mt-9 space-y-6">
          {[
            { t: "Transparent, Registered Business", d: "Registered Delhi-based IT company with clear communication, written SLAs and documented delivery processes." },
            { t: "Senior Engineering Team", d: "Every project is led by senior engineers — code reviews, security checks and QA are built into our workflow." },
            { t: "Messaging & API Expertise", d: "Hands-on experience with the official WhatsApp Business API, payment gateways and third-party integrations." },
            { t: "Support That Stays", d: "24×7 monitoring and post-launch support so your systems keep running long after go-live." },
          ].map((item, i) => (
            <Reveal key={item.t} delay={0.08 * i}>
              <div className="flex gap-4" data-testid={`why-us-item-${i}`}>
                <div className="shrink-0 w-10 h-10 rounded-full bg-gold/15 border border-gold/40 flex items-center justify-center">
                  <BadgeCheck size={18} className="text-gold" />
                </div>
                <div>
                  <h3 className="font-display font-bold text-ink text-lg">{item.t}</h3>
                  <p className="mt-1 text-sm text-slate-500 leading-relaxed">{item.d}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const CTABanner = () => (
  <section className="bg-ivory pb-24" data-testid="home-cta-section">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <Reveal>
        <div className="relative bg-royal rounded-[2rem] overflow-hidden px-8 py-14 sm:px-14 text-center grain">
          <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-[30rem] h-[30rem] rounded-full bg-white/10 blur-[90px]" />
          <h2 className="relative font-display text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight">
            Let's Build Something That Scales
          </h2>
          <p className="relative mt-4 text-blue-100 max-w-xl mx-auto">
            Share your idea with us — free consultation, transparent pricing and enterprise-grade delivery.
          </p>
          <div className="relative mt-8 flex flex-wrap justify-center gap-4">
            <Link
              to="/contact"
              data-testid="home-cta-contact-button"
              className="inline-flex items-center gap-2 bg-white text-royal font-bold px-8 py-3.5 rounded-full hover:-translate-y-1 transition-all duration-300 shadow-xl"
            >
              Contact Us <ArrowRight size={17} />
            </Link>
            <a
              href={`tel:+${SITE.phoneRaw}`}
              data-testid="home-cta-call-button"
              className="inline-flex items-center gap-2 border-2 border-white/60 text-white font-bold px-8 py-3.5 rounded-full hover:bg-white/10 hover:-translate-y-1 transition-all duration-300"
            >
              <Phone size={17} /> Call {SITE.phoneDisplay}
            </a>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

const Home = () => (
  <>
    <Hero />
    <Marquee />
    <Stats />
    <ServicesBento />
    <Estimator />
    <WhyUs />
    <CTABanner />
  </>
);

export default Home;
